import { computed, defineComponent, ref, useContext, useCssModule } from '@nuxtjs/composition-api';
export default defineComponent({
    name: 'Pagination',
    props: {
        currentPage: {
            default: 1,
            type: Number,
        },
        total: {
            required: true,
            type: Number,
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        /**
         * Composables.
         */
        const css = useCssModule();
        const context = useContext();
        /**
         * Current page
         */
        const current = ref(props.currentPage);
        /**
         * Items
         */
        const items = computed(() => {
            const itemsCount = context.$viewport.isLessThan('tablet') ? 7 : 9;
            if (props.total <= itemsCount) {
                return Array.from({ length: props.total }, (_, i) => i + 1);
            }
            const pages = [];
            const ellipsisItemsMinCount = 2;
            const shouldSetLeftEllipsis = current.value - ellipsisItemsMinCount >= ellipsisItemsMinCount;
            const shouldSetRightEllipsis = props.total - (current.value + 1) >= ellipsisItemsMinCount;
            // Set first page
            pages[0] = 1;
            // Set last page
            pages[itemsCount - 1] = props.total;
            // Set left side ellipsis
            if (shouldSetLeftEllipsis) {
                pages[1] = '&middot;&middot;&middot;';
            }
            // Set right side ellipsis
            if (shouldSetRightEllipsis) {
                pages[itemsCount - 2] = '&middot;&middot;&middot;';
            }
            const isCurrentClosestToFirstPage = current.value < itemsCount - ellipsisItemsMinCount;
            const isCurrentClosestToLastPage = current.value > props.total - (itemsCount - ellipsisItemsMinCount - 1);
            if (isCurrentClosestToFirstPage) {
                for (let i = 1; i < itemsCount - ellipsisItemsMinCount; i++) {
                    pages[i] = i + 1;
                }
            }
            else if (isCurrentClosestToLastPage) {
                for (let i = ellipsisItemsMinCount; i < itemsCount; i++) {
                    pages[i] = props.total - itemsCount + i + 1;
                }
            }
            else {
                const middleIndex = Math.floor((itemsCount - 1) / 2);
                for (let i = 0; i < itemsCount; i++) {
                    if (pages[i]) {
                        continue;
                    }
                    if (i < middleIndex) {
                        pages[i] = current.value - (middleIndex - i);
                    }
                    else if (i > middleIndex) {
                        pages[i] = current.value + (i - middleIndex);
                    }
                    else {
                        pages[i] = current.value;
                    }
                }
            }
            return pages;
        });
        const canGoLeft = computed(() => current.value > 1);
        const canGoRight = computed(() => current.value < props.total);
        function handlePagination(event) {
            const target = event.target;
            const button = target.closest('button:not(:disabled)');
            if (!button) {
                return;
            }
            const { dataset: { page }, } = button;
            if (!page) {
                return;
            }
            switch (true) {
                case page === 'prev':
                    if (current.value === 1) {
                        return;
                    }
                    current.value -= 1;
                    break;
                case page === 'next':
                    if (current.value === props.total) {
                        return;
                    }
                    current.value += 1;
                    break;
                case !isNaN(Number(page)) && typeof Number(page) === 'number':
                    current.value = Number(page);
                    break;
            }
            emit('change', current.value);
        }
        return {
            canGoLeft,
            canGoRight,
            css,
            current,
            items,
            handlePagination,
        };
    },
});
